//Oct28 AssetLibrary
/* ────────────────────────────────────────────────────────────────
   AssetLibrary.jsx   (direct-file upload, no AssetUploadDialog)
   ----------------------------------------------------------------
   • Header  – search bar + “add” button (+ progress bar while uploading)
   • Body    – scrollable AssetBoard
   • Hidden  <input type="file"> triggered by the add button
   • Duplicate-name handling now uses <ConfirmReplaceDialog>
───────────────────────────────────────────────────────────────── */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Toolbar,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import Tooltip           from '@mui/material/Tooltip';
import AddIcon           from '@mui/icons-material/Add';

import { useAuth }        from '../contexts/AuthContext';
import { useFormContext } from '../contexts/FormContext';

import GlobalSearchBar    from './GlobalSearchBar';
import AssetBoard         from './AssetBoard';
import ConfirmReplaceDialog from './ConfirmReplaceDialog';
import MetadataFilterPanel from './MetadataFilterPanel';
import UploadErrorSnackbar from './UploadErrorSnackbar';
import { preflightIcon } from '../utils/imagePreflight';
import AssetAccessPrompt from '../components/auth/AssetAccessPrompt';
import EmailPasswordLoginForm from '../components/auth/EmailPasswordLoginForm';

import { useNavigate, useLocation } from 'react-router-dom';


import {
  uploadAssetWithDefaultFolder,
  tagAssetWithForm,
  fetchAssets,
  fetchAssetById, //hydrate inserted assets with signed URLs
  buildStoragePublicUrl,
} from '../utils/frontend_assetManager';
import { resolveAssetVersionInfo } from '../utils/assetVersioning';
import { loadAssetPromotionConfig } from '../utils/assetPromotionConfig';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { decompressData } from '../utils/decompressionUtils';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storageUtils';
import { setAufPendingFile } from '../utils/aufHandoffStore';
import { applyTransformations } from '../utils/transformationUtils';


import './styles/assetManager.css';
import './styles/assetCard.css';

const AssetLibrary = ({
  isModal             = false,
  onClose,
  onSelectAsset,
  allowedImageTypes   = [],   // default to no restriction (generic)
  /** library operating context
   *  - 'manager' (default): full Studio behavior (can upload directly)
   *  - 'auf': AUF-aware picker behavior (Upload routes to AUF step)
   */
  mode                = 'manager',
  /** when in AUF mode, clicking Upload should ask AUF to open its attach+fields step
   *  You’ll pass this from the AUF surface. It can open a side sheet, focus
   *  the attach field, or switch to the AUF wizard step.
   *  Optionally accepts a payload: { file?: File } if you later want drag/drop passthrough.
   */
  onRequestUploadNew  = null,
  /** allow handoff to AUF from non-AUF surfaces (Flow 3) */
  enableAufHandoff    = false,
  /**
   * Auth-aware picker context.
   * - public-attach: attach field opened from a public standalone form
   * - asset-library: default app/library behavior
   */
  authPromptContext   = 'asset-library',
  /**
   * Embedded forms should not initialize inline auth inside the iframe.
   * Instead, show a prompt that opens the standalone form in a new window.
   */
  embeddedMode        = false,
  /**
   * Used only when embeddedMode=true.
   * Should point to /forms/:formId for the standalone public form.
   */
  standaloneFormUrl   = null,
}) => {
  const navigate = useNavigate();
 const location = useLocation();

  /* ───────────── auth/context ───────────── */
  const {
    organizationId,
    currentUser,
    userPermissions,
    getToken,
    authLoading,
    initialized,
  } = useAuth();

  const { formId } = useFormContext();

  /* ───────────── local state ───────────── */
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading ,  setUploading ]  = useState(false);
  const [aufIdState, setAufIdState]   = useState(null);  // hold org AUF id (if any)
  const [showInlineLogin, setShowInlineLogin] = useState(false);

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: asset picker access state
  // - Public standalone forms can show login-in-place.
  // - Embedded forms must not initialize auth inside the iframe.
  // - AssetBoard must not mount until auth/org/permissions are ready.
  // ──────────────────────────────────────────────────────────────
  const assetAccessState = useMemo(() => {
    if (!isModal) return 'ready';

    if (embeddedMode) {
      return 'embeddedOpenForm';
    }

    const authInitialized = initialized !== false;
    const authStillLoading = Boolean(authLoading) || !authInitialized;
    const permissionsReady = Array.isArray(userPermissions);

    if (authStillLoading && !currentUser) {
      return 'loadingAccount';
    }

    if (!currentUser) {
      return 'standaloneLogin';
    }

    if (authStillLoading) {
      return 'loadingAccount';
    }

    if (!organizationId) {
      return 'missingOrganization';
    }

    if (!permissionsReady) {
      return 'loadingAccount';
    }

    if (!userPermissions.includes('viewAsset')) {
      return 'noPermission';
    }

    return 'ready';
  }, [
    authLoading,
    currentUser,
    embeddedMode,
    initialized,
    isModal,
    organizationId,
    userPermissions,
  ]);

  const shouldRenderAssetPicker = assetAccessState === 'ready';

  // ──────────────────────────────────────────────────────────────
  // upload error UI (snackbar)
  // - Surfaces client preflight failures (e.g. icon too large)
  // - Surfaces upload failures (permissions/network/backend validation)
  // - Keeps the user on the same screen with a clear action to retry
  // ──────────────────────────────────────────────────────────────
  const [uploadErrorOpen, setUploadErrorOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  // Filter bar state (fileType/tags/collection/date/form scope)
  const [filters, setFilters] = useState({
    fileType: '',
    tags: [],
    collectionId: '',
    dateFrom: '',
    dateTo: '',
    formIdFilter: null,   // toggled by "This form" switch
  });

  // ── Promoted facets (from org-level assetPromotionConfig) ─────────
  // Shape: { keys: string[], arrayKeys: string[], dateKeys: string[] }
  const [promotedFacets, setPromotedFacets] = useState({
    keys: [], arrayKeys: [], dateKeys: []
  });

  // Values the user sets for those promoted facets (to send to backend)
  // equals:   { UFI: string }
  // arrayAny: { UFI: string[] }
  // dateRange:{ UFI: { start?: string|number, end?: string|number } }
  const [promotedFilters, setPromotedFilters] = useState({
    equals: {}, equalsAny: {}, arrayAny: {}, dateRange: {}
  });


  // UFI → human label map for promoted controls (from the org's Asset Upload Form)
  const [promotedLabels, setPromotedLabels] = useState({});
   // UFI → choices[] for select fields (from catalog or AUF dropdownOptions)
   const [promotedChoices, setPromotedChoices] = useState({});

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: catalog hydration marker
  // - When org.assetFilterCatalog exists, we treat it as source-of-truth
  //   for filter schema (labels/choices/facets) to avoid AUF decompress work
  //   on every open.
  // - We use the catalog version as a stable guard (no race on first open).
  // ──────────────────────────────────────────────────────────────
  const [catalogVersion, setCatalogVersion] = useState(null);

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: system registry from assetFilterCatalog.
  //
  // Example:
  //   assetFilterCatalog.system.fields.assetType = {
  //     kind: 'enum',
  //     ufi,
  //     map: { screenshot: optionId },
  //     reverse: { [optionId]: 'screenshot' }
  //   }
  //
  // This lets the Asset Library resolve the canonical Asset Type field
  // without depending on the editable field label "Asset type".
  // ──────────────────────────────────────────────────────────────
  const [catalogSystem, setCatalogSystem] = useState({});

  // ──────────────────────────────────────────────────────────────
  // Normalize catalog choices into the object shape the UI expects:
  //   { value: optionId|string, label: string }
  // Back-compat: older catalogs stored choices as string[] labels.
  // ──────────────────────────────────────────────────────────────
  const normalizeCatalogChoices = useCallback((rawChoices) => {
    const out = {};
    const src = rawChoices && typeof rawChoices === 'object' ? rawChoices : {};

    Object.entries(src).forEach(([ufi, arr]) => {
      if (!Array.isArray(arr)) return;

      // New shape: [{ value, label, systemValue, ... }]
      //
      // IMPORTANT:
      // buildAssetFilterCatalog stores optionId in `value` because filter
      // payloads use optionId as the canonical submitted/filter value.
      //
      // We also expose `optionId` as an alias for older UI paths that still
      // check match.optionId. This keeps catalog-backed choices and AUF
      // decompressed choices aligned.
      if (arr.length && typeof arr[0] === 'object' && arr[0] !== null) {
        out[ufi] = arr
          .map((c) => {
            const value = c?.value != null ? String(c.value).trim() : '';
            const label = c?.label != null ? String(c.label).trim() : value;
            if (!value || !label) return null;

            const optionId =
              c?.optionId != null
                ? String(c.optionId).trim()
                : value;

            const rawLabel =
              c?.rawLabel != null
                ? String(c.rawLabel).trim()
                : label;

            const systemValue =
              c?.systemValue != null
                ? String(c.systemValue).trim()
                : '';

            const nextChoice = {
              ...c,
              value,
              label,
              optionId,
              rawLabel,
            };

            // Preserve explicit system identity when present.
            // Do not infer it here; catalog/backend owns system semantics.
            if (systemValue) {
              nextChoice.systemValue = systemValue;
            }

            return nextChoice;
          })
          .filter(Boolean);
        return;
      }

      // Legacy shape: string[] labels → treat as {value: label, label: label}
      out[ufi] = arr
        .map((s) => (s == null ? '' : String(s).trim()))
        .filter(Boolean)
        .map((s) => ({ value: s, label: s, optionId: null, rawLabel: s }));
    });

    return out;
  }, []);
   

  /* ───────────── detect AUF Asset type facet (premium + configured) ───────────── */
  const assetTypeUfi = useMemo(() => {
    // ENTERPRISE:
    // Prefer the catalog system registry. Field labels are editable and should
    // not be the primary way to identify platform semantics.
    const systemUfi =
      catalogSystem?.fields?.assetType?.ufi != null
        ? String(catalogSystem.fields.assetType.ufi).trim()
        : '';

    if (systemUfi) {
      return systemUfi;
    }

    // Backward-compatible fallback for older orgs/catalogs.
    const keys = promotedFacets?.keys || [];
    for (const ufi of keys) {
      const label = promotedLabels?.[ufi];
      if (
        typeof label === 'string' &&
        label.trim().toLowerCase() === 'asset type'
      ) {
        return ufi;
      }
    }
    return null;
  }, [catalogSystem, promotedFacets?.keys, promotedLabels]);

  // Consider DAM “fully ready” for replacing Type when:
  //  • org has a published AUF (aufIdState set from organizations.assetUploadFormId)
  //  • AUF has a promoted field labeled "Asset type"
  const hideLegacyType = Boolean(aufIdState && assetTypeUfi);

  // If you also want to hide Tags when AUF Asset type exists, you can gate that here too.
  // For now we keep Tags always visible:
  const hideTags = false;

  // ──────────────────────────────────────────────────────────────
  // Build backend-ready promotedFilters payload.
  // If legacy Type (filters.fileType) is used, translate it into
  // equalsAny for the AUF assetTypeUfi so backend can filter by optionId.
  // ──────────────────────────────────────────────────────────────
  const effectivePromotedFilters = useMemo(() => {
    const pf =
      promotedFilters && typeof promotedFilters === 'object'
        ? promotedFilters
        : { equals: {}, equalsAny: {}, arrayAny: {}, dateRange: {} };

    const next = {
      equals: pf.equals || {},
      equalsAny: pf.equalsAny || {},
      arrayAny: pf.arrayAny || {},
      dateRange: pf.dateRange || {},
    };

    // Legacy Type dropdown → translate to AUF equalsAny when available.
    // This ensures Asset Type filtering works even if the user never
    // touches the promoted facet control.
    const legacyType =
      filters?.fileType && typeof filters.fileType === 'string'
        ? filters.fileType.trim().toLowerCase()
        : '';

    if (assetTypeUfi && legacyType) {
      const choices = Array.isArray(promotedChoices?.[assetTypeUfi])
        ? promotedChoices[assetTypeUfi]
        : [];

      // Preferred: match by stable systemValue when present.
      // Fallbacks: opt.value (your canonical slug), then rawLabel normalization.
      const norm = (s) =>
        String(s || '')
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, '');

      const needle = norm(legacyType);

      const hit = choices.find((c) => {
        if (c?.systemValue) return norm(c.systemValue) === needle;
        if (c?.value) return norm(c.value) === needle;
        if (c?.rawLabel) return norm(c.rawLabel) === needle;
        return false;
      });

      if (hit?.value) {
        next.equalsAny = {
          ...next.equalsAny,
          [assetTypeUfi]: [hit.value],
        };
      }
    }

    return next;
  }, [
    promotedFilters,
    assetTypeUfi,
    promotedChoices,
    filters?.fileType,
  ]);

  /* duplicate-name dialog state */
  const [dupDialogOpen , setDupDialogOpen ] = useState(false);
  const [pendingFile   , setPendingFile   ] = useState(null);   // File object
  const [pendingToken  , setPendingToken  ] = useState(null);   // auth token
  const assetTypeRef = useRef(null);

   /* refs */
  const boardRef     = useRef(null);   // call refresh() after upload
  const fileInputRef = useRef(null);   // hidden file input
  const handoffInputRef = useRef(null); // hidden file input (Flow 3 pre-pick)

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: Hydrate an optimistically inserted asset with a
  // signed URL from the backend so AssetCard does NOT fall back to
  // Storage SDK getDownloadURL() (which can 403 on newly-inserted tiles).
  //
  // fetchAssetById returns shape like:
  //   { assetId, name, type, aspect, previewUrl?, meta? }
  //
  // We map previewUrl → thumbUrl + grid.u400 (+ cloudSrc) so the tile
  // can render immediately without any private Storage calls.
  // ──────────────────────────────────────────────────────────────
  const hydrateInsertedAsset = useCallback(async (assetId) => {
    try {
      if (!assetId) return;

      const token = await getToken?.();
      if (!token) {
        console.warn('[AssetLibrary] hydrateInsertedAsset: missing token; skipping hydration for', assetId);
        return;
      }

      const data = await fetchAssetById(assetId, token, {
        includeUrls: true,
        includeMeta: true,
        prefer: null,
      });

      const previewUrl =
        (data && typeof data.previewUrl === 'string' && data.previewUrl.trim())
          ? data.previewUrl.trim()
          : null;

      if (!data) {
        console.warn('[AssetLibrary] hydrateInsertedAsset: backend returned no payload for', assetId);
        return;
      }

      // Merge meta if present, but keep our UI-facing URL fields LAST (authoritative).
      const normalized = {
        ...(data?.meta && typeof data.meta === 'object' ? data.meta : {}),
        ...data,
        id: data.assetId || assetId,
        assetId: data.assetId || assetId,

        ...(previewUrl
          ? {
              thumbUrl: previewUrl,
              grid: { u400: previewUrl, u800: null },
              cloudSrc: previewUrl,
            }
          : {}),
      };

      console.log('[AssetLibrary] hydrateInsertedAsset: updating tile with hydrated asset:', {
        id: normalized.id,
        hasPreviewUrl: Boolean(previewUrl),
        hasThumbUrl: Boolean(normalized.thumbUrl),
        hasGrid400: Boolean(normalized.grid?.u400),
      });

      boardRef.current?.updateAssetById?.(normalized.id, normalized);
    } catch (e) {
      console.warn('[AssetLibrary] hydrateInsertedAsset failed (non-fatal):', e?.message || e);
    }
  }, [getToken, fetchAssetById]);

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: Live insert for AUF uploads when /assets is mounted
  // behind the AUF dialog (navbar flow).
  //
  // PublishedFormPage dispatches:
  //   window.dispatchEvent(new CustomEvent('asset:created', { detail: { asset } }))
  //
  // We prepend immediately so the grid reflects "newest asset" without refresh.
  // This runs once per mount (listener), and is effectively idle otherwise.
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onCreated = (e) => {
      try {
        const d = e?.detail || {};
        const route = d?.originRoute || null;
        const a = d?.asset || null;

        // Only respond when the user came from /assets
        if (route !== '/assets') return;
        if (!a || !(a.id || a.assetId)) return;

        console.log('[AssetLibrary] asset:created received → prepending:', {
          id: a.id || a.assetId,
          name: a.name,
        });

        // Ensure stable id field (AssetBoard dedupe relies on this)
        const normalized = a.id ? a : { ...a, id: a.assetId || a.id };

        boardRef.current?.prependAsset?.(normalized);

        // ENTERPRISE: hydrate to signed URL so AssetCard doesn't 403 on Storage fallbacks
        hydrateInsertedAsset(normalized.id);
      } catch (err) {
        console.warn('[AssetLibrary] asset:created handler failed:', err?.message || err);
      }
    };

    window.addEventListener('asset:created', onCreated);

    // Also consume a one-shot bridge if present (covers the edge case where
    // /assets mounts after closing the modal instead of being “behind” it).
    try {
      const bridge = safeGetItem('assets_return_bridge');
      if (bridge?.originRoute === '/assets' && bridge?.asset) {
        const a = bridge.asset;
        const normalized = a.id ? a : { ...a, id: a.assetId || a.id };

        console.log('[AssetLibrary] assets_return_bridge consumed → prepending:', {
          id: normalized.id,
          name: normalized.name,
        });

        boardRef.current?.prependAsset?.(normalized);

        // ENTERPRISE: hydrate to signed URL so AssetCard doesn't 403 on Storage fallbacks
        hydrateInsertedAsset(normalized.id);

        safeRemoveItem('assets_return_bridge'); // one-shot
      }
    } catch (_) {}

    return () => window.removeEventListener('asset:created', onCreated);
  }, [hydrateInsertedAsset]);

  // scroll root ref — this is the element that actually scrolls
  // (your .asset-library-content Box has overflow: auto in assetManager.css)
  // We pass this to AssetBoard so IntersectionObserver tracks the correct scroll container.
  const scrollRootRef = useRef(null);

  // Global unmount safety for optimistic blob previews
  // If the library unmounts while an upload is in flight,
  // make sure we revoke the last created object URL.
  const lastBlobRef = useRef(null);


  useEffect(() => {
    return () => {
      try {
        if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
      } catch (_) {}
    };
  }, []);

  /* ───────────── DEV: clear stale cached facets for this org ───────────── */
  useEffect(() => {
    if (!organizationId) return;

    // ENTERPRISE: do not bust caches in production unless explicitly debugging.
    const shouldBust =
      typeof window !== 'undefined' && window.__DEBUG_ASSETLIB_CACHE_BUST__ === true;

    if (!shouldBust) return;

    try {
      const k = `org_${organizationId}_assetPromotionConfig`;
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    } catch (e) {
      console.warn('[DEV] cache bust failed:', e?.message || e);
    }
  }, [organizationId]);

  /* ───────────── TEMP: log org-level promotion config ───────────── */
  useEffect(() => {
    (async () => {
      if (!organizationId) return;
      try {
        const snap = await getDoc(doc(db, 'organizations', organizationId));
        const raw  = snap.exists() ? snap.data()?.assetPromotionConfig : null;
        console.log('[DEBUG:org.assetPromotionConfig]', raw || '(none)');
      } catch (e) {
        console.warn('[DEBUG:org.assetPromotionConfig] failed:', e?.message || e);
      }
    })();
  }, [organizationId]);

  /* ───────────── RESET on org switch ───────────── */
  useEffect(() => {
    // Clear catalog-derived UI state
    setPromotedLabels({});
    setPromotedChoices({});
    setCatalogVersion(null);
    setCatalogSystem({});
    setAufIdState(null);

    // Clear promoted filter selections (equals / equalsAny / arrayAny / dateRange)
    setPromotedFilters({ equals: {}, equalsAny: {}, arrayAny: {}, dateRange: {} });
  }, [organizationId]);


  /* ───────────── load org-level promoted facets ───────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!organizationId) return;
      try {
       // 1) Single org read → prefer materialized catalog
       const orgSnap = await getDoc(doc(db, 'organizations', organizationId));
       const org = orgSnap.exists() ? orgSnap.data() : {};
       const cat = org?.assetFilterCatalog || null;

       if (cat && !cancelled) {
         setPromotedFacets(
           cat.facets || { keys: [], arrayKeys: [], dateKeys: [] }
         );
         setPromotedLabels(cat.labels || {});
         setPromotedChoices(normalizeCatalogChoices(cat.choices || {}));
         setCatalogSystem(cat.system || {});

         // ENTERPRISE: mark catalog as the source-of-truth for this open
         setCatalogVersion(cat.version || null);

         console.log('[AssetLibrary] hydrated from catalog:', {
           keys    : (cat?.facets?.keys || []).length,
           labels  : Object.keys(cat?.labels || {}).length,
           choices : Object.keys(cat?.choices || {}).length,
           hasAssetTypeSystem:
             Boolean(cat?.system?.fields?.assetType?.ufi),
         });
         return; // done; skip AUF blob path
       }

        // Force a fresh Firestore read after org/form resets
        const cfg = await loadAssetPromotionConfig(organizationId, { bypassCache: true }); // { keys, arrayKeys, dateKeys }
        if (!cancelled) setPromotedFacets(cfg);
        // Re-cache fresh for next time
        safeSetItem(`org_${organizationId}_assetPromotionConfig`, cfg);

        console.log('[AssetLibrary] promotedFacets (final):', {
          keys      : (cfg?.keys || []).length,
          arrayKeys : (cfg?.arrayKeys || []).length,
          dateKeys  : (cfg?.dateKeys || []).length,
          sampleKeys: (cfg?.keys || []).slice(0, 3),
        });
      } catch (e) {
        console.warn('[AssetLibrary] Failed to load assetPromotionConfig:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);


/* ───────────── PRUNE selections when facet buckets change ───────────── */
useEffect(() => {
  setPromotedFilters((prev) => {
    const k = new Set(promotedFacets.keys || []);
    const a = new Set(promotedFacets.arrayKeys || []);
    const d = new Set(promotedFacets.dateKeys || []);

    const keepEq = Object.fromEntries(
      Object.entries(prev.equals || {}).filter(([ufi]) => k.has(ufi))
    );
    const keepEqAny = Object.fromEntries(
      Object.entries(prev.equalsAny || {}).filter(([ufi]) => k.has(ufi))
    );
    const keepArr = Object.fromEntries(
      Object.entries(prev.arrayAny || {}).filter(([ufi]) => a.has(ufi))
    );
    const keepDate = Object.fromEntries(
      Object.entries(prev.dateRange || {}).filter(([ufi]) => d.has(ufi))
    );

    const changed =
      Object.keys(keepEq).length    !== Object.keys(prev.equals || {}).length   ||
      Object.keys(keepEqAny).length !== Object.keys(prev.equalsAny || {}).length||
      Object.keys(keepArr).length   !== Object.keys(prev.arrayAny || {}).length ||
      Object.keys(keepDate).length  !== Object.keys(prev.dateRange || {}).length;

    return changed
      ? { equals: keepEq, equalsAny: keepEqAny, arrayAny: keepArr, dateRange: keepDate }
      : prev;
  });
}, [
  promotedFacets.keys?.length,
  promotedFacets.arrayKeys?.length,
  promotedFacets.dateKeys?.length,
]);




 /* ───────────── load UFI → label map from org's Asset Upload Form ───────────── */
 useEffect(() => {
   let cancelled = false;
   (async () => {
     if (!organizationId) return;
    // No promoted facets? Nothing to label yet.
    if (
      (promotedFacets.keys?.length ?? 0) +
      (promotedFacets.arrayKeys?.length ?? 0) +
      (promotedFacets.dateKeys?.length ?? 0) === 0
    ) return;

     // If we have labels already, fine — but still rebuild choices.
     // Do NOT return early here; choices may be stale or empty.
     const haveLabels  = Object.keys(promotedLabels || {}).length > 0;
     const haveChoices = Object.keys(promotedChoices || {}).length > 0;
     // (intentionally no return)

     // ────────────────────────────────────────────────────────────────
     // ENTERPRISE: Prefer the org-level assetFilterCatalog as the
     // authoritative filter schema. If it exists (catalogVersion is set),
     // skip AUF decompress work on open (expensive + causes extra renders).
     //
     // Fallback: if catalogVersion is not set but we already have labels
     // and choices in state, also skip.
     // ────────────────────────────────────────────────────────────────
     if (catalogVersion) {
       return; // ✅ catalog hydrated; skip AUF decompress path
     }
     if (haveLabels && haveChoices) {
       return; // ✅ already hydrated via cache; skip AUF decompress path
     }
    
     try {
       // instant cached paint
       const cacheKey = `org_${organizationId}_aufLabelMap`;
       const cached = safeGetItem(cacheKey);
       if (cached && !cancelled) setPromotedLabels(cached);

      // fast-paint for select choices (same pattern as labels)
      const choicesCacheKey = `org_${organizationId}_aufChoicesMap`;
      const cachedChoices = safeGetItem(choicesCacheKey);
      if (cachedChoices && !cancelled) {
        setPromotedChoices(cachedChoices);
      }

       // 1) read organizations/{orgId}.assetUploadFormId
       const orgRef = doc(db, 'organizations', organizationId);
       const orgSnap = await getDoc(orgRef);
       const aufId = orgSnap.exists() ? orgSnap.data()?.assetUploadFormId : null;
       if (!aufId) return; // AUF not published yet
      console.log('[AssetLibrary] AUF id:', aufId);
      if (!cancelled) setAufIdState(aufId || null);    // ← NEW

       // 2) read forms/{aufId}.fieldsMetadata (compressed)
const formRef = doc(db, 'forms', aufId);
const formSnap = await getDoc(formRef);
if (!formSnap.exists()) {
  console.warn('[AssetLibrary] AUF form doc not found for', aufId);
  return;
}
const compressed = formSnap.data()?.fieldsMetadata;
if (!compressed) {
  console.warn('[AssetLibrary] AUF fieldsMetadata is missing or empty for', aufId);
  return;
}
 console.log('[AssetLibrary] AUF fieldsMetadata: found compressed blob (length ~)', String(compressed?.length || '').slice(0, 8), '…');


 // 3) decompress → build UFI → label
 let fields;
 try {
   fields = await decompressData(compressed);
 } catch (e) {
   console.error('[AssetLibrary] decompressData failed:', e?.message || e);
   return;
 }
 const map = {};
 const entries = Object.values(fields || {});
 console.log('[AssetLibrary] AUF decompressed fields count:', entries.length);
 entries.forEach((f) => {
   const ufi = f?.ufi;
   if (!ufi) return;
   map[ufi] = f.label || f.name || f.id || ufi;
 });
 if (!cancelled) setPromotedLabels(map);
 // cache fresh
 safeSetItem(cacheKey, map);

// ▼▼▼ NEW: build UFI → choices[] for select fields from AUF dropdownOptions
try {
// ▼ NEW: build UFI → choices[] as transformed labels (value/label) + optionId
const optsB64 = formSnap.data()?.dropdownOptions;
let choices = {};

if (optsB64) {
  const opts = await decompressData(optsB64);

  // For each field, if it's select and has optionsSetUFI, map to { value,label,optionId }[]
  const selectUFIs = entries.filter(
    (f) => f?.type === 'single-select' || f?.type === 'multi-select'
  );

  selectUFIs.forEach((f) => {
    const ufi    = f?.ufi;
    const setUfi = f?.optionsSetUFI;
    if (!ufi || !setUfi) return;

    const setArr = Array.isArray(opts?.[setUfi]) ? opts[setUfi] : [];
    const mapped = setArr
      .map((o) => {
        if (!o?.optionId || !o?.label) return null;

        const rawLabel         = o.label;
        const transformedLabel = applyTransformations(
          rawLabel,
          f?.transformations || {}
        );

        const systemValue =
          o?.systemValue != null
            ? String(o.systemValue).trim()
            : '';

        const nextChoice = {
          // Canonical filter value – MUST be optionId
          value   : o.optionId,
          // Human-visible label (respect field transformations)
          label   : transformedLabel,
          // Extra metadata for future debugging/migrations
          optionId: o.optionId,
          rawLabel,
        };

        // Preserve explicit option-level system identity when present.
        // Do not infer it here; AUF schema/catalog creation owns semantics.
        if (systemValue) {
          nextChoice.systemValue = systemValue;
        }

        return nextChoice;
      })
      .filter(Boolean);


    if (mapped.length > 0) {
      choices[ufi] = mapped;
    }
  });
}

  if (!cancelled) {
    setPromotedChoices(choices);

    // DEBUG: show a small sample + the particular UFI (if present)
    console.log(
      '[AssetLibrary] AUF choices sample:',
      Object.entries(choices).slice(0, 3)
    );
    const CHK = Object.keys(map)[0]; // any UFI present
    if (CHK && choices[CHK]) {
      console.log(
        '[AssetLibrary] AUF choices for %s : %o',
        CHK,
        choices[CHK]
      );
    }

    // Optional: cache per-org choices map too, if you like a quick paint later
    safeSetItem(`org_${organizationId}_aufChoicesMap`, choices);
  }

} catch (e) {
  console.warn('[AssetLibrary] Failed to load AUF dropdownOptions:', e?.message || e);
}



 // DEBUG: map size + sample + a specific UFI check
 const DEBUG_UFI = '29bd78d2'; // prefix you see in UI
 console.log('[AssetLibrary] AUF label map size:', Object.keys(map).length);
 console.log('[AssetLibrary] AUF labels sample:', Object.entries(map).slice(0, 5));
 if (DEBUG_UFI) {
   const hit = Object.keys(map).find(k => k.startsWith(DEBUG_UFI));
   if (hit) console.log('[AssetLibrary] DEBUG_UFI resolved:', { ufi: hit, label: map[hit] });
   else console.log('[AssetLibrary] DEBUG_UFI not found in AUF map:', DEBUG_UFI);
 }


     } catch (e) {
       console.warn('[AssetLibrary] Failed to load AUF labels:', e?.message || e);
     }
   })();
 }, [
   organizationId,
   promotedFacets.keys?.length,
   promotedFacets.arrayKeys?.length,
   promotedFacets.dateKeys?.length,
   catalogVersion,
 ]);

  /* ───────────── helpers ───────────── */

 // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: normalize upload errors into a user-friendly message
  // - Handles: permission gating, auth token issues, network failures,
  //            backend validation (dimensions), and generic errors
  // - Keeps all upload UX messaging consistent across the library
  // ──────────────────────────────────────────────────────────────
  const normalizeUploadError = useCallback((err, ctx = {}) => {
    try {
      const raw =
        (err && typeof err === 'object' && typeof err.message === 'string')
          ? err.message
          : (typeof err === 'string' ? err : '');

      const assetType =
        (ctx && typeof ctx.assetType === 'string' && ctx.assetType.trim())
          ? ctx.assetType.trim().toLowerCase()
          : null;

      // Common auth/token failures
      if (!raw || raw.includes('Firebase ID Token is undefined') || raw.includes('could not authenticate')) {
        return 'Upload failed: could not authenticate. Please refresh and try again.';
      }

      // Permission failures
      if (
        raw.toLowerCase().includes('do not have permission') ||
        raw.toLowerCase().includes('missing permission') ||
        raw.toLowerCase().includes('permission')
      ) {
        return 'Upload failed: you do not have permission to upload assets.';
      }

      // Network / timeout / connectivity
      if (
        raw.toLowerCase().includes('failed to fetch') ||
        raw.toLowerCase().includes('network') ||
        raw.toLowerCase().includes('timeout') ||
        raw.toLowerCase().includes('aborted')
      ) {
        return 'Upload failed due to a network issue. Please check your connection and try again.';
      }

      // Dimension / validation mapping (backend messages)
      // Examples from your backend:
      // - "Invalid width for icon. Maximum allowed width is 250px, but got 512px."
      // - "Invalid height for icon..."
      // - "Invalid dimensions for icon. Minimum dimensions are ..."
      // - "Invalid dimensions for banner. Minimum dimensions are 800x200..."
      if (raw.includes('Invalid width for icon')) {
        return 'Icon is too wide. Max allowed width is 250px.';
      }
      if (raw.includes('Invalid height for icon')) {
        return 'Icon is too tall. Max allowed height is 150px.';
      }
      if (raw.includes('Invalid dimensions for icon') || raw.includes('Minimum dimensions are')) {
        // If backend already provides a nice "Minimum dimensions are ..." message, keep it
        return raw;
      }
      if (raw.includes('Invalid dimensions for banner')) {
        // Keep your UI copy consistent with what you show in the settings panel
        return 'Banner dimensions are invalid. Please choose a properly sized banner image.';
      }

      // File type / format errors (backend / sharp)
      if (raw.toLowerCase().includes('unsupported image type')) {
        return 'Unsupported image format. Please upload a PNG, JPG/JPEG, or WebP.';
      }
      if (raw.toLowerCase().includes('failed to retrieve image metadata') || raw.toLowerCase().includes('metadata')) {
        return 'Failed to read image metadata. Please confirm this is a valid image file.';
      }

      // Fallback: keep original message if it’s already user-friendly
      if (raw && raw.length <= 180) {
        return raw;
      }

      // Final fallback
      return 'Upload failed. Please check your file and try again.';
    } catch (_) {
      return 'Upload failed. Please check your file and try again.';
    }
  }, []);

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: resolve Storage path → browser-usable URL for UI
  // - For public-eligible derivatives (icon/banner/screenshot + grids/thumbs),
  //   use storage.googleapis.com directly (no rules, no 403 spam).
  // - Otherwise return null and let the caller decide the next step.
  //
  // IMPORTANT
  //  • The bucket must come from the active Firebase environment.
  //  • Never hardcode production bucket names in UI code.
  //  • In staging, this must resolve to REACT_APP_FIREBASE_STORAGE_BUCKET.
  // ──────────────────────────────────────────────────────────────
  const buildPublicUrl = (objectPath) => {
    const url = buildStoragePublicUrl(objectPath);

    if (!url) {
      console.warn('[AssetLibrary] Could not build public Storage URL:', {
        hasObjectPath: Boolean(objectPath),
      });
    }

    return url;
  };

  const isEligibleForPublicFallback = (p, assetType = null) => {
    if (!p || typeof p !== 'string') return false;
    if (!p.startsWith('assets/')) return false;

    // only these asset types are guaranteed public in Storage.
    const PUBLIC_TYPES = new Set(['icon', 'banner', 'screenshot']);
    const t = String(assetType || '').trim().toLowerCase();
    if (!PUBLIC_TYPES.has(t)) return false;

    return (
      /\/v\d+_thumbnail\.(webp|png|jpg|jpeg)$/i.test(p) ||
      /\/v\d+_grid_(400|800)\.webp$/i.test(p) ||
      /\/v\d+_micro_(32|64)\.webp$/i.test(p) ||
      /\/v\d+_original\.(webp|png|jpg|jpeg)$/i.test(p) ||
      /\/v\d+_icon2x\.(webp|png|jpg|jpeg)$/i.test(p) ||
      /\/v\d+_icon\.(webp|png|jpg|jpeg)$/i.test(p) ||
      /\/v\d+_banner2x\.(webp|png|jpg|jpeg)$/i.test(p) ||
      /\/v\d+_banner\.(webp|png|jpg|jpeg)$/i.test(p)
    );
  };

  const toUiUrl = (maybePathOrUrl, assetType = null) => {
    if (!maybePathOrUrl) return null;
    const s = String(maybePathOrUrl);

    // Already a usable URL
    if (
      s.startsWith('http://') ||
      s.startsWith('https://') ||
      s.startsWith('data:') ||
      s.startsWith('blob:')
    ) {
      return s;
    }

    // Storage path → public URL ONLY for truly-public asset types
    if (isEligibleForPublicFallback(s, assetType)) {
      return buildPublicUrl(s);
    }

    // Private path: let the caller decide (do not attempt getDownloadURL here)
    return null;
  };

  const handleModalClose = () => {
    setSearchQuery('');
    setShowInlineLogin(false);
    onClose?.();
  };

  const handleAssetAccessPrimary = useCallback(() => {
    if (assetAccessState === 'embeddedOpenForm') {
      if (!standaloneFormUrl) return;

      window.open(
        standaloneFormUrl,
        '_blank',
        'noopener,noreferrer',
      );

      return;
    }

    if (assetAccessState === 'standaloneLogin') {
      setShowInlineLogin(true);
    }
  }, [
    assetAccessState,
    standaloneFormUrl,
  ]);

  const handleInlineLoginSuccess = useCallback(() => {
    // Do not close the modal and do not navigate.
    // AuthContext will hydrate org/permissions, and AssetLibrary will
    // naturally swap into AssetBoard once assetAccessState becomes "ready".
    setShowInlineLogin(false);
  }, []);

  const renderAccessState = () => {
    if (assetAccessState === 'standaloneLogin' && showInlineLogin) {
      return (
        <EmailPasswordLoginForm
          onSuccess={handleInlineLoginSuccess}
          onCancel={handleModalClose}
          titleId="asset-login-dialog-title"
          descriptionId="asset-login-dialog-description"
          formId="asset-library-inline-login-form"
        />
      );
    }

    return (
      <AssetAccessPrompt
        state={assetAccessState}
        onPrimary={handleAssetAccessPrimary}
        onSecondary={handleModalClose}
        primaryDisabled={
          assetAccessState === 'embeddedOpenForm' &&
          !standaloneFormUrl
        }
        titleId="asset-access-dialog-title"
        descriptionId="asset-access-dialog-description"
      />
    );
  };

  /** trigger hidden input */
  const openFilePicker = () => fileInputRef.current?.click();

  /**
   * Flow 3: called when the user picked a file in the original click stack.
   * Stash the File in memory and ask the caller to open AUF as a dialog.
   * AUF will read this file from the in-memory handoff bus on mount.
   */
const handleHandoffPick = async (e) => {
  try {
    console.log('[AssetLibrary] handoff onChange fired');
    const f = e.target.files?.[0] || null;
    console.log('[AssetLibrary] handoff selected:', f?.name || '(none)');
    // Reset input value so the same file can be picked again later
    if (handoffInputRef.current) handoffInputRef.current.value = '';
    if (!f) return;

      // 1) Stash in memory for AUF to consume immediately on mount
      setAufPendingFile(f);

      // 2) Ask caller to open AUF dialog (keeps your existing handoff shape)
      if (onRequestUploadNew && typeof onRequestUploadNew === 'function') {
        onRequestUploadNew({ intent: 'openAuf', aufId: aufIdState });
      }
    } catch (err) {
      console.warn('[AssetLibrary] handleHandoffPick failed:', err);
    }
  };

  /** Core uploader — shared by “no duplicate” + dialog outcomes */
const startUpload = async ({
  file,
  token,
  assetType,
  versionMeta  = {},   // { previousAssetId, versionNumber }
  overrideName = null, // string | null
}) => {
  // ── HOIST to make them visible in finally ─────────────────────────
  let unmountCleanup = () => {};                // assigned below
  const localUrlRef  = { current: null };       // last optimistic blob
  const abortRevoke  = new AbortController();   // guard double-revoke
  const uploadSucceededRef = { current: false }; // prevent early blob revoke on success
  // ──────────────────────────────────────────────────────────────────

  try {
    setUploading(true);

    console.log('[AssetLibrary] Uploading:', {
      name      : file.name,
      sizeKB    : Math.round(file.size / 1024),
      assetType,
      versionMeta,
      overrideName,
    });

    // ──────────────────────────────────────────────────────
    // Local optimistic preview (blob:) for immediate paint.
    // We’ll revoke it AFTER the card swaps to cloud.
    // ──────────────────────────────────────────────────────
    let localUrl = null;

    // define the cleanup now that helpers are hoisted
    unmountCleanup = () => {
      if (abortRevoke.signal.aborted) return;
      try { if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current); } catch {}
      abortRevoke.abort();
    };

    try {
      localUrl = URL.createObjectURL(file);
      localUrlRef.current = localUrl;  // ← set AFTER createObjectURL
      lastBlobRef.current = localUrl;  // ← let the global unmount cleanup see it
    } catch (e) {
      console.warn('[AssetLibrary] createObjectURL failed (non-fatal):',
        e?.message || e);
    }

      /* ───── UPLOAD ───── */

      // Base metadata for all uploads (ImageField + library uploads)
      const baseAdditionalMeta = {
        formId,
        tags: [],
        canonicalName: (overrideName || file.name).toLowerCase(),
        ...versionMeta,
      };

      // Optional AUF bridge: if this org has a published AUF and a
      // canonical Asset Type field, populate its response from the
      // ImageField assetType ('icon' | 'banner' | 'screenshot', etc.).
      let bridgeResponses = null;

      try {
        // assetTypeRef.current was set in handleFileChange
        const slug = assetTypeRef.current;  // e.g. 'icon'
        if (
          aufIdState &&
          slug &&
          assetTypeUfi &&
          Array.isArray(promotedChoices?.[assetTypeUfi]) &&
          promotedChoices[assetTypeUfi].length > 0
        ) {
          const choices = promotedChoices[assetTypeUfi];
          const needle  = String(slug).toLowerCase();

          const norm = (s) =>
            String(s || '')
              .trim()
              .toLowerCase()
              .replace(/[\s_-]+/g, '');

          const needleNorm = norm(needle);

          // Enterprise-stable match order:
          // 1) systemValue (from catalog)  2) option.value (slug)
          // 3) rawLabel normalization (legacy)
          const match = choices.find((opt) => {
            if (opt?.systemValue) return norm(opt.systemValue) === needleNorm;
            if (opt?.value) return norm(opt.value) === needleNorm;
            if (opt?.rawLabel) return norm(opt.rawLabel) === needleNorm;
            return false;
          });

          const matchedOptionId =
            match?.optionId != null
              ? String(match.optionId).trim()
              : (
                  match?.value != null
                    ? String(match.value).trim()
                    : ''
                );

          if (match && matchedOptionId) {
            const labelKey = `label_${assetTypeUfi}`;
            bridgeResponses = {
              [assetTypeUfi]: { value: matchedOptionId },
              [labelKey]: promotedLabels?.[assetTypeUfi] || 'Asset type',
            };

            console.log('[AssetLibrary] AUF bridge for asset type:', {
              ufi: assetTypeUfi,
              slug: slug,
              optionId: matchedOptionId,
              systemValue: match?.systemValue || null,
              label: match.rawLabel,
            });
          } else {
            console.log(
              '[AssetLibrary] AUF bridge: no matching choice for',
              slug,
              'on UFI',
              assetTypeUfi
            );
          }
        }
      } catch (e) {
        console.warn(
          '[AssetLibrary] AUF bridge failed (non-fatal):',
          e?.message || e
        );
      }

      const additionalMetadata = bridgeResponses
        ? { ...baseAdditionalMeta, responses: bridgeResponses }
        : baseAdditionalMeta;

      const uploaded = await uploadAssetWithDefaultFolder(
        file,
        organizationId,
        currentUser.uid,
        additionalMetadata,
        null,                    // default folder
        userPermissions,
        assetType,
        token,
        overrideName
      );

      console.log('[AssetLibrary] Upload success:', uploaded);
      // do NOT mark success for blob cleanup yet.
      // Success (for cleanup) means: we have a path that will revoke the blob
      // after swapping to a cloud URL, or we never created a blob.

      // if no blob URL was created, there is nothing to revoke.
      // Mark as "succeeded" so finally does not run unmountCleanup() unnecessarily.
      if (!localUrl) uploadSucceededRef.current = true;

      // Normalize .id so downstream always has a stable key
      const norm = uploaded.id ? uploaded : { ...uploaded, id: uploaded.assetId };

      // If we have a local blob URL, attach it so AssetCard paints instantly
      if (localUrl) {
        norm.localSrc = localUrl;
      }

      // ENTERPRISE: if backend returned a usable thumbUrl (some endpoints do), keep it.
      // Otherwise, try to derive a safe first-paint URL from thumb.urls["400"].
      // This avoids any getDownloadURL() fallback and prevents 403 spam.
      if (!norm.thumbUrl) {
        const firstPaint = toUiUrl(norm?.thumb?.urls?.['400'], assetType);
        if (firstPaint) {
          norm.thumbUrl = firstPaint;
        }
      }

      /* 🔄 prepend – don’t refetch the whole board */
      boardRef.current?.prependAsset?.(norm);

      // ENTERPRISE: hydrate newly inserted tiles to a signed URL for private assets.
      // - Public types (icon/banner/screenshot) can use public URLs (toUiUrl may resolve them).
      // - Private DAM assets must NOT rely on Storage paths or public URL guessing.
      // - If we don't have a thumbUrl yet, fetchAssetById will return a signed previewUrl.
      try {
        const PUBLIC_TYPES = new Set(['icon', 'banner', 'screenshot']);
        const t = String(assetType || '').trim().toLowerCase();
        const isPublicType = PUBLIC_TYPES.has(t);

        const idForHydrate = norm?.id || norm?.assetId || null;
        const hasThumbUrl =
          typeof norm?.thumbUrl === 'string' && norm.thumbUrl.trim() !== '';

        if (!isPublicType && idForHydrate && !hasThumbUrl) {
          hydrateInsertedAsset(idForHydrate);
        }
      } catch (e) {
        console.warn('[AssetLibrary] hydrate-after-prepend failed (non-fatal):', e?.message || e);
      }

      // If this came back as a *placeholder* (202 path), wait for final
      if (uploaded?.status === 'processing' &&
          typeof uploaded.__waitForFinal === 'function') {
        try {
          const finalDoc = await uploaded.__waitForFinal();
          const id = finalDoc?.id || finalDoc?.assetId || norm.id;

          // Promote to finalized doc in-place (preserve local image for one paint)
          boardRef.current?.updateAssetById?.(id, {
            ...finalDoc,
            // cloud 400w (if present) gives us the first real network preview
            // IMPORTANT: thumb.urls["400"] is a STORAGE PATH, not a URL.
            // Convert to a browser-usable URL so AssetCard does not fall back to getDownloadURL().
            cloudSrc: toUiUrl(finalDoc?.thumb?.urls?.['400'], assetType) || null,
            localSrc: norm.localSrc || null,
          });

          // ENTERPRISE: after worker finalization, re-hydrate to signed URLs for private assets.
          // This ensures the tile converges to the best available preview (grid/micro) without refresh.
          try {
            const PUBLIC_TYPES = new Set(['icon', 'banner', 'screenshot']);
            const t = String(assetType || '').trim().toLowerCase();
            const isPublicType = PUBLIC_TYPES.has(t);

            if (!isPublicType && id) {
              hydrateInsertedAsset(id);
            }
          } catch (e) {
            console.warn('[AssetLibrary] hydrate-after-finalize failed (non-fatal):', e?.message || e);
          }

         // only now that the new version is finalized,
         //    hide the previous tile so the grid never shows a hole on failure.
         if (versionMeta?.previousAssetId) {
           console.log('[AssetLibrary] Finalized new version → removing previous tile:', versionMeta.previousAssetId);
           boardRef.current?.removeAssetById?.(versionMeta.previousAssetId);
         }

         // Revoke the blob after the card has a tick to paint the cloud
         if (localUrl) {
           // mark "success" only once we have a deterministic
           // blob cleanup path (swap → delay → revoke).
           uploadSucceededRef.current = true;

           setTimeout(() => {
             try { URL.revokeObjectURL(localUrl); } catch {}
             boardRef.current?.updateAssetById?.(id, { localSrc: null });
           }, 300);
         }
       } catch (e) {
         console.warn('[AssetLibrary] waitForFinal failed (non-fatal):',
           e?.message || e);
         // leave the placeholder; user can refresh later
       }
      } else if (localUrl) {
        // Legacy 200 path – processed immediately. Swap to cloud 400 if available.
        const id = norm.id;
        const cloud400 = uploaded?.thumb?.urls?.['400'] || null;
        if (cloud400) {
          // mark "success" only once we have a deterministic
          // blob cleanup path (swap → delay → revoke).
          uploadSucceededRef.current = true;

          setTimeout(() => {
            try { URL.revokeObjectURL(localUrl); } catch {}
            boardRef.current?.updateAssetById?.(id, {
              localSrc: null,
              // IMPORTANT: uploaded.thumb.urls["400"] is a STORAGE PATH.
              // Convert to browser URL so AssetCard can preload it.
              cloudSrc: toUiUrl(cloud400, assetType) || null,
            });

            // ENTERPRISE: re-hydrate private assets after swap so the tile gets signed URLs.
            try {
              const PUBLIC_TYPES = new Set(['icon', 'banner', 'screenshot']);
              const t = String(assetType || '').trim().toLowerCase();
              const isPublicType = PUBLIC_TYPES.has(t);

              if (!isPublicType && id) {
                hydrateInsertedAsset(id);
              }
            } catch (e) {
              console.warn('[AssetLibrary] hydrate-after-swap failed (non-fatal):', e?.message || e);
            }

          }, 300);
        }
       // In the rare legacy 200 path, you *may* also remove the old tile now:
       if (versionMeta?.previousAssetId) {
         console.log('[AssetLibrary] Upload returned 200 immediately → removing previous tile:', versionMeta.previousAssetId);
         boardRef.current?.removeAssetById?.(versionMeta.previousAssetId);
       }

      }


    } catch (err) {
      console.error('[AssetLibrary] Upload failed:', err);

      // ENTERPRISE: normalize to a user-friendly message (consistent across all failures)
      const msg = normalizeUploadError(err, { assetType });

      setUploadErrorMessage(msg);
      setUploadErrorOpen(true);
    } finally {
      setUploading(false);

      /* reset <input type=file> so selecting the same file triggers change */
      if (fileInputRef.current) fileInputRef.current.value = '';

      /* clear pending state */
      // only revoke blob on failure/unmount.
      // Successful paths revoke the blob AFTER swapping to cloud.
      if (!uploadSucceededRef.current) {
        unmountCleanup();
      }
      setPendingFile(null);
      setPendingToken(null);
      setDupDialogOpen(false);
    }
  };

  /** ─────────────────────────────────────────────────────────────
      UPLOAD FLOW
      • duplicate-name check
      • open dialog if needed
      • resolve next version or copy name per user choice
      • call startUpload()
     ──────────────────────────────────────────────────────────── */
  const handleFileChange = async (e) => {
   const files = e?.target?.files;
   console.log('[AssetLibrary] <input type=file> onChange fired', {
     hasEvent: !!e, filesLength: files?.length, firstName: files?.[0]?.name
   });
 const file = files?.[0];

if (!file) return;

// clear previous upload error when user selects a new file
if (uploadErrorOpen) setUploadErrorOpen(false);
if (uploadErrorMessage) setUploadErrorMessage('');

const token = await getToken();
    if (!token) {
      console.warn('[AssetLibrary] getToken() returned null/undefined. Aborting upload.');
setUploadErrorMessage(normalizeUploadError(new Error('could not authenticate'), { assetType: null }));
      setUploadErrorOpen(true);
      return;
    } else {
      console.log('[AssetLibrary] Got auth token for upload');
    }

    // Resolve type safely:
    //  • If caller provided types (Image field), respect first one ('icon'/'banner')
    //  • Else (Attach field), treat as generic → assetType = null
    const type = Array.isArray(allowedImageTypes) && allowedImageTypes.length
      ? allowedImageTypes[0]
      : null;

    assetTypeRef.current = type; // remember for later (no render)
   console.log('[AssetLibrary] Resolved assetType for upload:', type ?? '(generic)');

    // ─────────────────────────────────────────────────────────────
    // AUF MODE: do NOT upload directly from the Library.
    // Kick back to the AUF entry (attach + fields) so the AUF
    // submits once (with answers) and the backend can promote fields.
    // If you later want a drag/drop passthrough, you can pass { file }.
    // ─────────────────────────────────────────────────────────────
    if (mode === 'auf') {
     console.log('[AssetLibrary] Mode=auf → deferring upload to AUF path');

      try {
        onRequestUploadNew?.({ file }); // hand the File back to the AUF field
      } catch (err) {
        console.warn('[AssetLibrary] AUF handoff failed:', err);
      } finally {
        // reset the input so selecting the same file again triggers change
        if (fileInputRef.current) fileInputRef.current.value = '';
        // close the modal *after* the user picked a file (prevents flicker)
        if (isModal) handleModalClose();
      }
      return; // ← stop: no duplicate check; AUF handles versioning on submit
    }

    // Optional: client preflight for icons to surface friendlier errors
    if (type === 'icon') {
     console.log('[AssetLibrary] Icon preflight starting…');

      try {
        const res = await preflightIcon(file);
        if (!res.ok) {
          console.warn('[AssetLibrary] Icon preflight:', res.message);

          // ENTERPRISE: surface preflight failure immediately in UI
          setUploadErrorMessage(res.message || 'Icon preflight failed. Please try a different image.');
          setUploadErrorOpen(true);
console.log('[AssetLibrary] Snackbar set:', {
  open: true,
  message: res.message || '(fallback)',
});
          return;
        }
      } catch (pfErr) {
        console.warn('[AssetLibrary] Icon preflight failed:', pfErr);
        // Not fatal; backend still enforces
      }
    }

    console.log('[AssetLibrary] Candidate upload:', {
      name   : file.name,
      sizeKB : Math.round(file.size / 1024),
      assetType: type,
    });

    /* ───── 1. DUPLICATE-NAME CHECK (org-scoped, latest only) ───── */
    let duplicate = null;
    try {
     console.log('[AssetLibrary] Running duplicate-name check for', file.name);
      const dupResp = await fetchAssets(
        organizationId,
        null,              // collectionId
        [],                // tags
        file.name,         // name prefix
        5,                 // small page
        null,              // lastVisible
        token,
        null               // form filter
      );

    console.log('[AssetLibrary] dupResp assets length:', dupResp?.assets?.length ?? 0);

    // Treat “generic” as no-typed match (null or undefined in stored doc)
    duplicate = dupResp.assets?.find((a) => {
      const sameName =
        (a.canonicalName || '').toLowerCase() === file.name.toLowerCase();

      if (!sameName) return false;

      // If we resolved a concrete type (icon/banner), require exact match
      if (type) return a.assetType === type;

      // Generic attach: consider docs with null/undefined type as duplicates
      return a.assetType == null; // true for null or undefined
    });

    console.log(
      '[AssetLibrary] duplicate result:',
      duplicate ? { id: duplicate.id || duplicate.assetId, name: duplicate.name, assetType: duplicate.assetType } : '(none)'
    );
    } catch (err) {

      console.error('[AssetLibrary] Duplicate-check failed — proceeding anyway:', err);
    }

    /* ───── 2a. …NO duplicate ➜ upload immediately ───── */
    if (!duplicate) {
     console.log('[AssetLibrary] No duplicate found → uploading as NEW');

      await startUpload({ file, token, assetType: type });
      return;
    }

    /* ───── 2b. Duplicate found ➜ show <ConfirmReplaceDialog> ───── */
   console.log('[AssetLibrary] Duplicate found → opening Replace/Copy dialog');

    setPendingFile(file);
    setPendingToken(token);
    setDupDialogOpen(true);
  };

  /* ───────────── duplicate-dialog callbacks ───────────── */
  const handleReplace = async () => {
    console.log('[AssetLibrary] Replace clicked in dialog');

    if (!pendingFile) return;

    /* Look up next version number & previous id */
    let versionMeta = {};
    try {
      versionMeta = await resolveAssetVersionInfo({
        assetType    : assetTypeRef.current,
        organizationId,
        formId,
        fileName     : pendingFile.name,
      });
      console.log('[AssetLibrary] REPLACE chosen – versionMeta:', versionMeta);

     // Do NOT remove the previous card yet — wait for a successful upload AND finalization.
     // The safe removal happens inside startUpload() after we swap to finalDoc.
    } catch (err) {
      console.error('[AssetLibrary] Failed to resolve version info:', err);
    }

    try {
      await startUpload({
        file       : pendingFile,
        token      : pendingToken,
        assetType  : assetTypeRef.current,
        versionMeta,
        overrideName: null,
      });
     // If the upload failed (throws), we never remove the old card (keeps grid stable).
    } catch (e) {
      console.warn('[AssetLibrary] Replace upload failed; keeping previous tile visible:', e?.message || e);
    }
  };


  const handleCopy = async () => {
   console.log('[AssetLibrary] Copy clicked in dialog');

    if (!pendingFile) return;

    /* Generate a unique copy name – base_copy_<yyyymmddHHMMSS> */
    const ts   = new Date().toISOString().replace(/[-:.TZ]/g, '');
    const dot  = pendingFile.name.lastIndexOf('.');
    const base = dot >= 0 ? pendingFile.name.slice(0, dot) : pendingFile.name;
    const ext  = dot >= 0 ? pendingFile.name.slice(dot)    : '';
    const overrideName = `${base}_copy_${ts}${ext}`;

    console.log('[AssetLibrary] COPY chosen – new name:', overrideName);

    await startUpload({
      file       : pendingFile,
      token      : pendingToken,
//      assetType,
      assetType    : assetTypeRef.current,
      overrideName,
      versionMeta: {}, // none
    });
  };

  /* ───────────── header (white) ───────────── */
  const renderHeader = () => (
    <Box
      sx={{
        bgcolor: '#fff',
        //borderBottom: '1px solid',
        //borderColor: (theme) => theme.palette.divider,
        px: 4,
        pt: 2,
        pb: 1,
      }}
    >
      {/* Row 1: search + upload */}
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 0,
          py: 0,
          minHeight: 56,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <GlobalSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Search assets by name, tag, or metadata"
          />
        </Box>

      {/* add/upload */}
        <Box>
          <Tooltip title="Upload file" arrow>
            <span>
              <IconButton
                className="upload-button"
                disabled={uploading || !userPermissions?.includes('uploadAsset')}
onClick={() => {
  // DEBUG: make same-file reselection fire 'change'
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
    console.log('[AssetLibrary] Upload button clicked → cleared file input value');
  }
 if (mode === 'auf') {
   // AUF’s own surface → keep native picker
   console.log('[AssetLibrary] Mode=auf → opening native picker');
   fileInputRef.current?.click();
 } else {
   // Manager/library surfaces: ALWAYS use direct picker so duplicate-check/dialog can run
   console.log('[AssetLibrary] Manager view → opening direct picker (duplicate-check enabled)');
   openFilePicker();
 }
}}

              >
                {uploading ? <CircularProgress size={20} /> : <AddIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {/* Hidden input remains in AssetLibrary so we preserve the
              user gesture and avoid closing the dialog before the
              picker appears. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {/* Flow 3: a separate hidden picker used BEFORE opening AUF,
              so we keep the original user gesture for the native picker */}
          <input
            ref={handoffInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleHandoffPick}
          />

        </Box>
      </Toolbar>

      {/* Row 2: compact filter bar (chips + dropdowns) */}
      <Box
        sx={{
          pt: 0,
          pb: 0.5,
        }}
      >
        <MetadataFilterPanel
          filters={filters}
          setFilters={setFilters}
          availableTags={[]} 
          availableFileTypes={['icon','banner','screenshot']}
          hideLegacyType={hideLegacyType}
          availableCollections={[]}               // [{id,name}] when ready
          currentFormId={formId}

          promotedFacets={promotedFacets}
          promotedValues={promotedFilters}
          onPromotedChange={setPromotedFilters}
          promotedLabels={promotedLabels}
          promotedChoices={promotedChoices}
        />
      </Box>
    </Box>
  );

  /* ───────────── body ───────────── */
  const renderBody = () => (
    <Box className="asset-library-body">
      <Box className="asset-library-content" ref={scrollRootRef}>
        <AssetBoard
          ref={boardRef}
          boardId={organizationId}
          initialFormId={null}            // show all assets on open
          searchQuery={searchQuery}
          promotedFilters={effectivePromotedFilters}
          assetTypeFilter={
            assetTypeUfi ? null : (filters?.fileType || null)
          }

          // tell AssetBoard what element is actually scrolling
          scrollRootRef={scrollRootRef}

          onDetails={(info, asset) => {
            try {
              // If a child reported a delete, do nothing:
              // - AssetBoard already removed the tile optimistically
              // - Stay on the library, no navigation
              if (info && info.deleted) return;

              // For “open details” actions (e.g., card click),
              // pull id from the proper source.
              const id =
                asset?.id ||
                asset?.assetId ||
                info?.assetId ||
                null;

              if (id) {
                if (isModal) handleModalClose();
                navigate(`/assets/${id}`, { state: { modalFrom: location } });
              }
            } catch (e) {
              console.warn(
                '[AssetLibrary] navigate details failed:',
                e?.message || e
              );
            }
          }}


          // (Optional) pass filters through as you wire backend support:
          // tags={filters.tags}
          // assetType={filters.fileType}
          // collectionId={filters.collectionId}
          // dateFrom={filters.dateFrom}
          // dateTo={filters.dateTo}
          // formIdFilter={filters.formIdFilter}
           onUploadRequest={() => {
             if (mode === 'auf') {
               onRequestUploadNew?.();
             } else if (enableAufHandoff && aufIdState) {
               onRequestUploadNew?.({ intent: 'openAuf', aufId: aufIdState });
             } else {
               openFilePicker();
             }
           }}

          onSelectAsset={async (asset) => {
            const id = asset?.id || asset?.assetId;

            /* tag only if we’re inside the form-builder */
            if (formId && id) {
              try {
                console.log('[AssetLibrary] Tagging asset with formId (txn-safe):', {
                  assetId: id,
                  formId,
                });

                // Transaction-safe helper handles:
                // - asset missing
                // - asset deleted between paint and click
                // - avoids the getDoc → update race window
                await tagAssetWithForm(id, formId);

              } catch (err) {
                // Your helper already treats race failures as non-fatal,
                // but keep this log for visibility in case of permission/config issues.
                console.error(
                  '[AssetLibrary] Failed to tag asset with formId:',
                  err
                );
              }
            }

            /* hand the selection back to the caller */
            onSelectAsset?.(asset);

            if (isModal) handleModalClose();
          }}


          showMoreOptions
        />
      </Box>
    </Box>
  );



  /* ───────────── wrapper (modal vs inline) ───────────── */
  const shell = isModal ? (
    <Dialog
      open
      onClose={handleModalClose}
      fullWidth
      maxWidth={shouldRenderAssetPicker ? 'lg' : 'sm'}
      aria-labelledby={
        shouldRenderAssetPicker
          ? 'asset-library-dialog'
          : (
              showInlineLogin
                ? 'asset-login-dialog-title'
                : 'asset-access-dialog-title'
            )
      }
      aria-describedby={
        shouldRenderAssetPicker
          ? undefined
          : (
              showInlineLogin
                ? 'asset-login-dialog-description'
                : 'asset-access-dialog-description'
            )
      }
      PaperProps={{
        sx: {
          height       : shouldRenderAssetPicker ? '90vh' : 'auto',
          display      : 'flex',
          flexDirection: 'column',
     bgcolor      : '#fff',    // explicit white, optional
     p            : 0,         // critical: no Paper padding
     overflow     : 'hidden',  // avoid tiny white slivers
        },
      }}
    >
      {shouldRenderAssetPicker ? (
        <>
          {renderHeader()}
          {uploading && <LinearProgress color="info" sx={{ height: 2 }} />}
          {renderBody()}
        </>
      ) : (
        renderAccessState()
      )}
    </Dialog>
  ) : (
    <>
      {renderHeader()}
      {uploading && <LinearProgress color="info" sx={{ height: 2 }} />}
      {renderBody()}
    </>
  );

  /* ───────────── render ───────────── */
  return (
    <>
      {shell}

      {/* duplicate-name prompt (re-usable component) */}
      <ConfirmReplaceDialog
        open={dupDialogOpen}
        fileName={pendingFile?.name || ''}
        onReplace={handleReplace}
        onCopy={handleCopy}
        onClose={() => {
          setDupDialogOpen(false);
          setPendingFile(null);
          setPendingToken(null);
        }}
      />
      {/* upload/preflight failures are surfaced immediately */}
      <UploadErrorSnackbar
        open={uploadErrorOpen}
        message={uploadErrorMessage}
        onClose={() => setUploadErrorOpen(false)}
      />


    </>
  );
};

export default AssetLibrary;
